/**
 * Review Funnel Page (public, no login) — the "SLIK review version".
 *
 * Flow: client taps the link → rates 1–5 → we record it (first-party asset) → then:
 *   • rating ≥ threshold (default 5) → big Google CTA (trapped + timestamped).
 *   • rating <  threshold → private "what went wrong" note to the owner FIRST, and STILL
 *     a secondary public-review link (compliant: we never hide Google from anyone).
 *
 * Try it: /review/demo  (synthetic, writes nothing).
 * BEHAVIOR-PIN: SLIK review funnel — project-slik-pos review-request system.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Star, Scissors, CheckCircle, Heart } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  ReviewSettings, ReviewRequest, getReviewSettings, getReviewRequest,
  recordStarRating, recordGoogleClick, submitPrivateFeedback, googleReviewUrl,
} from '../services/reviewFunnelService';

type Stage = 'loading' | 'notfound' | 'rate' | 'public' | 'private' | 'done';

const ReviewFunnelPage: React.FC = () => {
  const { token = '' } = useParams();
  const [settings, setSettings] = useState<ReviewSettings | null>(null);
  const [req, setReq] = useState<ReviewRequest | null>(null);
  const [stage, setStage] = useState<Stage>('loading');
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const s = await getReviewSettings();
      const r = await getReviewRequest(token);
      setSettings(s);
      setReq(r);
      setStage(r ? 'rate' : 'notfound');
    })();
  }, [token]);

  const chooseRating = async (n: number) => {
    if (!req || !settings) return;
    setRating(n);
    setBusy(true);
    try { await recordStarRating(req, n); } catch { /* non-blocking */ }
    setBusy(false);
    setStage(n >= settings.publicThreshold ? 'public' : 'private');
  };

  const goGoogle = async () => {
    if (!req || !settings) return;
    try { await recordGoogleClick(req); } catch { /* non-blocking */ }
    window.location.href = googleReviewUrl(settings);
  };

  const sendPrivate = async () => {
    if (!req) return;
    setBusy(true);
    try {
      await submitPrivateFeedback(req, rating, feedback.trim());
      setStage('done');
    } catch {
      toast.error('Could not send — please try again.');
    }
    setBusy(false);
  };

  const shell = (children: React.ReactNode) => (
    <div className="min-h-screen bg-gradient-to-br from-slik-navy via-slik-purple to-slik-magenta flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center">
        <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-slik-purple to-slik-magenta flex items-center justify-center">
          <Scissors className="w-7 h-7 text-white" />
        </div>
        {children}
      </div>
    </div>
  );

  if (stage === 'loading') return shell(<p className="text-gray-500">Loading…</p>);

  if (stage === 'notfound') return shell(
    <>
      <h1 className="text-xl font-bold text-slik-navy mb-2">Link expired</h1>
      <p className="text-gray-500">This review link is no longer active. Thanks for stopping by!</p>
    </>
  );

  const name = req?.clientName && req.clientName !== 'there' ? req.clientName : '';
  const barber = req?.barberName || 'your barber';

  if (stage === 'rate') return shell(
    <>
      <h1 className="text-2xl font-bold text-slik-navy mb-1">{settings?.headline || 'How was your visit?'}</h1>
      <p className="text-gray-500 mb-6">{name ? `Thanks ${name}! ` : ''}How was your cut with {barber}?</p>
      <div className="flex justify-center gap-2 mb-2">
        {[1, 2, 3, 4, 5].map(n => (
          <button
            key={n}
            disabled={busy}
            onClick={() => chooseRating(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            className="p-1 transition-transform hover:scale-110 disabled:opacity-50"
            aria-label={`${n} star${n > 1 ? 's' : ''}`}
          >
            <Star className={`w-11 h-11 ${(hover || rating) >= n ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-400">Tap a star</p>
    </>
  );

  if (stage === 'public') return shell(
    <>
      <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
      <h1 className="text-2xl font-bold text-slik-navy mb-2">Amazing — thank you! 🎉</h1>
      <p className="text-gray-500 mb-6">{settings?.thankYouPublic}</p>
      <button
        onClick={goGoogle}
        className="w-full py-4 rounded-xl bg-gradient-to-r from-slik-purple to-slik-magenta text-white font-bold text-lg shadow-lg hover:opacity-95 transition-opacity"
      >
        Leave a Google review →
      </button>
      <p className="text-xs text-gray-400 mt-3">Opens Google — takes about 20 seconds.</p>
    </>
  );

  if (stage === 'private') return shell(
    <>
      <Heart className="w-12 h-12 text-slik-purple mx-auto mb-3" />
      <h1 className="text-2xl font-bold text-slik-navy mb-2">We want to make it right</h1>
      <p className="text-gray-500 mb-4">What could we have done better? This goes straight to the owner.</p>
      <textarea
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        rows={4}
        placeholder="Tell us what happened…"
        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-slik-purple outline-none resize-none mb-3"
      />
      <button
        onClick={sendPrivate}
        disabled={busy || !feedback.trim()}
        className="w-full py-4 rounded-xl bg-gradient-to-r from-slik-purple to-slik-magenta text-white font-bold text-lg shadow-lg hover:opacity-95 transition-opacity disabled:opacity-50"
      >
        {busy ? 'Sending…' : 'Send private feedback'}
      </button>
      {/* Compliant: everyone still has a path to Google — we never hide it. */}
      <button onClick={goGoogle} className="mt-4 text-sm text-gray-400 underline hover:text-slik-purple">
        Prefer to leave a public review instead?
      </button>
    </>
  );

  // done (after private feedback)
  return shell(
    <>
      <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
      <h1 className="text-2xl font-bold text-slik-navy mb-2">Thank you</h1>
      <p className="text-gray-500">{settings?.thankYouPrivate}</p>
    </>
  );
};

export default ReviewFunnelPage;
